import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, ShoppingCart, User, Heart, Menu, X, ChevronDown } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_8e64f860-a2c3-4880-99a2-0e37e54f65c6/artifacts/lq6grz4s_CB3D4111-0127-441A-AEB4-FB146D6CBB1E.png";

const categories = [
  { name: 'Electronics', slug: 'cat-electronics' },
  { name: 'Home & Garden', slug: 'cat-home' },
  { name: 'Fashion', slug: 'cat-fashion' },
  { name: 'Kitchen', slug: 'cat-kitchen' },
  { name: 'Sports & Outdoors', slug: 'cat-sports' },
  { name: 'Beauty & Personal Care', slug: 'cat-beauty' },
  { name: 'Toys & Games', slug: 'cat-toys' },
  { name: 'Baby & Kids', slug: 'cat-baby' },
  { name: 'Automotive', slug: 'cat-automotive' },
  { name: 'Books & Media', slug: 'cat-books' },
  { name: 'Pet Supplies', slug: 'cat-pets' },
  { name: 'Health & Wellness', slug: 'cat-health' },
  { name: 'Office Supplies', slug: 'cat-office' },
  { name: 'Jewelry & Watches', slug: 'cat-jewelry' },
  { name: 'Tools & Hardware', slug: 'cat-tools' },
  { name: 'Adult Health & Supplements', slug: 'cat-adult-health' },
  { name: 'Sexual Wellness', slug: 'cat-sexual-wellness' },
];

export default function Header() {
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { itemCount } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
    }
  };

  return (
    <header className="sticky top-0 z-50 bg-[#003366] shadow-lg">
      {/* Top bar */}
      <div className="bg-[#002244] text-white text-sm py-2">
        <div className="max-w-[1440px] mx-auto px-4 flex justify-between items-center">
          <span>Free shipping on orders over $50</span>
          <div className="hidden md:flex gap-4">
            <Link to="/about" className="hover:text-[#FFD700] transition-colors">About</Link>
            <Link to="/contact" className="hover:text-[#FFD700] transition-colors">Contact</Link>
            <Link to="/track-order" className="hover:text-[#FFD700] transition-colors">Track Order</Link>
            <Link to="/faq" className="hover:text-[#FFD700] transition-colors">Help</Link>
          </div>
        </div>
      </div>

      {/* Main header */}
      <div className="max-w-[1440px] mx-auto px-4 py-3">
        <div className="flex items-center gap-4 md:gap-8">
          {/* Mobile menu button */}
          <button
            className="md:hidden text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          {/* Logo */}
          <Link to="/" className="flex-shrink-0" data-testid="logo-link">
            <img
              src={LOGO_URL}
              alt="IPVALUE Managements"
              className="h-12 md:h-14 object-contain"
            />
          </Link>

          {/* Search bar */}
          <form onSubmit={handleSearch} className="flex-1 max-w-2xl hidden md:flex">
            <div className="flex w-full">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="rounded-l-md rounded-r-none bg-slate-100 text-slate-700 hover:bg-slate-200 border-r border-slate-300 px-3"
                  >
                    All <ChevronDown size={16} className="ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-48">
                  <DropdownMenuItem onClick={() => navigate('/products')}>
                    All Categories
                  </DropdownMenuItem>
                  {categories.map((cat) => (
                    <DropdownMenuItem
                      key={cat.slug}
                      onClick={() => navigate(`/products?category=${cat.slug}`)}
                    >
                      {cat.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <Input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 h-10 rounded-none border-none focus-visible:ring-0 bg-white"
                data-testid="search-input"
              />
              <Button
                type="submit"
                className="rounded-l-none rounded-r-md bg-[#FFD700] hover:bg-[#F4C430] text-slate-900"
                data-testid="search-btn"
              >
                <Search size={20} />
              </Button>
            </div>
          </form>

          {/* Right side actions */}
          <div className="flex items-center gap-2 md:gap-4">
            {user ? (
              <>
                <Link
                  to="/account?tab=wishlist"
                  className="hidden md:flex flex-col items-center text-white hover:text-[#FFD700] transition-colors"
                  data-testid="wishlist-link"
                >
                  <Heart size={22} />
                  <span className="text-xs mt-0.5">Wishlist</span>
                </Link>
                <Link
                  to="/account"
                  className="hidden md:flex flex-col items-center text-white hover:text-[#FFD700] transition-colors"
                  data-testid="account-link"
                >
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />
                  ) : (
                    <User size={22} />
                  )}
                  <span className="text-xs mt-0.5">{user.name?.split(' ')[0] || 'Account'}</span>
                </Link>
              </>
            ) : (
              <Link
                to="/login"
                className="hidden md:flex flex-col items-center text-white hover:text-[#FFD700] transition-colors"
                data-testid="login-link"
              >
                <User size={22} />
                <span className="text-xs mt-0.5">Sign In</span>
              </Link>
            )}

            <Link
              to="/cart"
              className="relative flex flex-col items-center text-white hover:text-[#FFD700] transition-colors"
              data-testid="cart-link"
            >
              <div className="relative">
                <ShoppingCart size={22} />
                {itemCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-[#FFD700] text-slate-900 text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                    {itemCount > 99 ? '99+' : itemCount}
                  </span>
                )}
              </div>
              <span className="text-xs mt-0.5 hidden md:block">Cart</span>
            </Link>
          </div>
        </div>

        {/* Mobile search */}
        <form onSubmit={handleSearch} className="md:hidden mt-3">
          <div className="flex">
            <Input
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 h-10 rounded-l-md rounded-r-none border-none bg-white"
            />
            <Button
              type="submit"
              className="rounded-l-none rounded-r-md bg-[#FFD700] hover:bg-[#F4C430] text-slate-900"
            >
              <Search size={20} />
            </Button>
          </div>
        </form>
      </div>

      {/* Category navigation */}
      <div className="bg-[#002855] text-white">
        <div className="max-w-[1440px] mx-auto px-4">
          <nav className="hidden md:flex items-center gap-4 py-2 text-sm overflow-x-auto">
            <Link
              to="/products?deal=true"
              className="flex items-center gap-1 text-[#FFD700] font-semibold hover:text-[#F4C430] transition-colors whitespace-nowrap"
              data-testid="deals-link"
            >
              Today's Deals
            </Link>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-1 hover:text-[#FFD700] transition-colors whitespace-nowrap">
                  All Categories <ChevronDown size={14} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56 max-h-96 overflow-y-auto">
                {categories.map((cat) => (
                  <DropdownMenuItem
                    key={cat.slug}
                    onClick={() => navigate(`/products?category=${cat.slug}`)}
                  >
                    {cat.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            {categories.slice(0, 6).map((cat) => (
              <Link
                key={cat.slug}
                to={`/products?category=${cat.slug}`}
                className="hover:text-[#FFD700] transition-colors whitespace-nowrap"
                data-testid={`nav-${cat.slug}`}
              >
                {cat.name}
              </Link>
            ))}
            <Link
              to="/admin"
              className="ml-auto text-[#C5A059] hover:text-[#FFD700] transition-colors whitespace-nowrap"
              data-testid="admin-link"
            >
              Admin
            </Link>
          </nav>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-[#002855] text-white py-4 px-4">
          <nav className="flex flex-col gap-3">
            <Link
              to="/products?deal=true"
              className="text-[#FFD700] font-semibold"
              onClick={() => setMobileMenuOpen(false)}
            >
              Today's Deals
            </Link>
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/products?category=${cat.slug}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                {cat.name}
              </Link>
            ))}
            <hr className="border-[#003366]" />
            <Link to="/account" onClick={() => setMobileMenuOpen(false)}>Account</Link>
            <Link to="/about" onClick={() => setMobileMenuOpen(false)}>About</Link>
            <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>Contact</Link>
            <Link to="/faq" onClick={() => setMobileMenuOpen(false)}>Help Center</Link>
            <Link to="/admin" className="text-[#C5A059]" onClick={() => setMobileMenuOpen(false)}>Admin</Link>
          </nav>
        </div>
      )}
    </header>
  );
}
